"use client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Icon } from "@biom3/react";
import Image from "next/image";
import { useRouter } from "next/navigation";

export interface GameCardData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  backgroundImage: string;
  url: string;
  slug?: string;
}

interface GameCardProps {
  game: GameCardData;
}

const BackgroundImage = ({
  backgroundImage,
  name,
}: { backgroundImage: string; name: string }) => {
  return (
    <div className="absolute inset-0 z-0">
      <Image src={backgroundImage} alt={name} fill className="object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
      <div className="absolute inset-0 bg-black/20 shadow-[inset_0_0_100px_50px_rgba(0,0,0,0.3)]" />
    </div>
  );
};

const GameIcon = ({ icon, name }: { icon: string; name: string }) => {
  return (
    <div className="w-12 h-12 bg-black/50 rounded border-2 border-white/20 flex items-center justify-center">
      <Image
        src={icon}
        alt={name}
        width={48}
        height={48}
        className="object-contain"
      />
    </div>
  );
};

const StatusBadge = () => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 relative bg-purple-500 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">P</span>
      </div>
      <span className="text-[10px] font-normal text-white/70 tracking-[2px] uppercase">
        ON POLYGON
      </span>
    </div>
  );
};

const GameInfo = ({
  name,
  subtitle,
  description,
}: { name: string; subtitle: string; description: string }) => {
  return (
    <div className="space-y-1">
      <StatusBadge />

      <h3 className="text-[28px] font-bold text-white leading-[36px]">
        {name}
      </h3>
      <h4 className="text-[28px] font-bold text-white leading-[36px]">
        {subtitle}
      </h4>

      <p className="text-sm text-white/80 leading-5">{description}</p>
    </div>
  );
};

const ComingSoonButton = () => {
  return (
    <div className="w-48 h-12 pl-4 bg-black border-2 border-white rounded-3xl">
      <div className="flex items-center">
        <div className="w-8 h-8 pt-1 flex items-center">
          <Icon icon="PlayGame" sx={{ width: "24px", height: "24px" }} />
        </div>
        <div className="flex flex-col items-start">
          <span className="text-base font-semibold text-white">
            Coming Soon
          </span>
          <div className="flex items-center text-[10px] text-white/80 tracking-[0.4px]">
            <span>To Immutable Play</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FollowNowButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      className="w-48 h-12 pl-4 bg-black border-2 border-white rounded-3xl"
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className="w-8 h-8 pt-1 flex items-center">
          <Icon icon="PlayGame" sx={{ width: "24px", height: "24px" }} />
        </div>
        <span className="text-base font-semibold text-white">Follow Now</span>
      </div>
    </Button>
  );
};

export const GameCard = ({ game }: GameCardProps) => {
  const router = useRouter();
  return (
    <Card className="w-[393px] min-w-[393px] h-[520px] relative overflow-hidden bg-[#202020] border-0 flex-shrink-0">
      <BackgroundImage
        backgroundImage={game.backgroundImage}
        name={game.name}
      />

      <CardContent className="absolute inset-0 z-10 p-6 grid grid-rows-3 gap-6">
        <div className="flex items-start">
          <GameIcon icon={game.icon} name={game.name} />
        </div>

        <div className="flex items-center">
          <GameInfo
            name={game.name}
            subtitle={game.subtitle}
            description={game.description}
          />
        </div>

        {game.slug ? (
          <div className="flex items-end">
            <FollowNowButton
              onClick={() => {
                router.push(`/games/${game.slug}/`);
              }}
            />
          </div>
        ) : (
          <div className="flex items-end">
            <ComingSoonButton />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
